import { union, isString, pick, map, extend } from 'lodash';
/**
   * @ngdoc service
   * @name adjust.service:Breadcrumbs
   *
   * @description
   * Simple service for breadcrumbs management
   *
   */
function Breadcrumbs(Tracker, $q, configuration, App) {
  'ngInject';
  var _history;
  var _loading = false;

  return {
    buildTrackersHistory: buildTrackersHistory,
    renderHistory: renderHistory,
    getHistory: getHistory,
    resetHistory: resetHistory,
    formatHistory: formatHistory
  };

  /**
   * @ngdoc method
   * @name buildTrackersHistory
   * @methodOf adjust.service:Breadcrumbs
   *
   * @description
   * Builds the history for particular tracker in order to use it to generate breadcrumbs
   *
   * @param {Object} params current params
   * @param {string=} parentName parent name like app name
   * @param {string=} tabName current active tab
   * @returns {promise} A promise representing the state of the tracker and tracker's ancestors requests
   */
  function buildTrackersHistory(params, parentName, tabName) {

    var history = [];
    var trackers = [];
    var tracker = params.tracker || params.trackerToken;

    var parent = parentName ? [{
      token: null,
      label: parentName,
      isBaseLevel: true
    }] : null;

    var tab = (tabName && configuration.isBeta) ? [{
      token: null,
      label: tabName,
      isBaseLevel: true,
      isTab: true
    }] : null;

    _loading = true;

    return _getTrackerHistory(tracker)
      .then(function(result) {

        trackers = result;
        return _getSecretsView(params.secret_ids, params.appToken, tabName);

      })
      .then(function(secrets) {

        _loading = false;
        history = union(parent, tab, secrets, trackers);

        _setHistory(history);

        return history;

      });

  }

  /**
   * @ngdoc method
   * @name _getTrackerHistory
   * @methodOf adjust.service:Breadcrumbs
   *
   * @description
   * Get current tracker details
   *
   * @param {Object|string} currentTracker current tracker details or tracker token
   * @return trackers history promise
   * @private
   */
  function _getTrackerHistory(currentTracker) {

    if (!currentTracker) {
      return $q.when([]);
    }

    var trackerPromise = isString(currentTracker) ?
      Tracker.getTracker(currentTracker, true) :
      $q.when(currentTracker);

    return trackerPromise
      .then(function(tracker) {

        var current = pick(tracker, [
          'label',
          'token',
          'name',
          'settings'
        ]);

        var labels = tracker.path ? tracker.path.split('::') : [];
        var levels = map(Tracker.getTrackerLevels(), 'name');

        var ancestors = map(labels, function(label, index) {
          return {
            token: tracker[levels[index] + '_token'],
            label: label
          };
        });

        return [].concat(ancestors, current);

      })
      .catch(function() {
        return [{
          token: currentTracker,
          label: 'unknown'
        }];
      });

  }

  /**
   * @ngdoc method
   * @name _getSecretsView
   * @methodOf adjust.service:Breadcrumbs
   *
   * @description
   * Get secrets breadcrumbs
   *
   * @param {string} secretId current secret id
   * @param {string} appToken current app token
   * @param {string} tab current tab
   * @private
   */
  function _getSecretsView(secretId, appToken, tab) {

    if (tab !== 'Secrets' || !secretId) {
      return $q.when([]);
    }

    return App.getSecretNames(appToken)
      .then(function(secret) {

        return [{
          token: null,
          label: secret[secretId] || 'Secret ' + secretId
        }];

      });

  }

  /**
   * @ngdoc method
   * @name renderHistory
   * @methodOf adjust.service:Breadcrumbs
   *
   * @description
   * Render tracker history for display in trackers and cost data screens
   *
   * @param {Object=} tracker tracker details
   */
  function renderHistory(tracker) {

    return buildTrackersHistory({tracker: tracker})
      .then(function(result) {

        var currentTracker = result.pop();
        var history = result;
        var level = history.length;

        return {
          tracker: currentTracker,
          history: history,
          level: level,
          isLastLevel: level >= 2
        };

      });
  }

  /**
   * @ngdoc method
   * @name _setHistory
   * @methodOf adjust.service:Breadcrumbs
   *
   * @description
   * Saving history
   *
   * @param {Object} history object
   * @private
   */
  function _setHistory(history) {

    _history = history;

  }

  /**
   * @ngdoc method
   * @name getHistory
   * @methodOf adjust.service:Breadcrumbs
   *
   * @description
   * Saving history
   *
   * @returns {Object} history object
   */
  function getHistory() {

    if (_loading) {
      return null;
    }

    return _history;
  }

  /**
   * @ngdoc method
   * @name resetHistory
   * @methodOf adjust.service:Breadcrumbs
   *
   * @description
   * Reset history
   *
   */
  function resetHistory() {

    _history = null;

  }

  /**
   * @ngdoc method
   * @name formatHistory
   * @methodOf adjust.service:Breadcrumbs
   *
   * @description
   * Saving history
   *
   * @returns {array} collection of formatted history steps
   */
  function formatHistory(history, baseParams) {
    return map(history, function(step) {
      return {
        label: step.label,
        sref: 'apps.setup.trackers',
        params: extend({}, baseParams, { trackerToken: step.token })
      };
    });
  }
}

export { Breadcrumbs };
